/**
 * Ключ доступа к сервису (docs/08-screens.md, docs/09-auth-security.md).
 *
 * Только добавление и замена: значение ключа наружу не отдаётся никогда,
 * в интерфейсе видна маска из последних четырёх символов.
 */
import { useEffect, useState } from 'react';
import { Badge, Button, Dialog, Icon, Input } from '@/ds';
import { Provenance } from '@/components/money';
import { ApiError } from '@/api/client';
import { useOperation, type ServiceSummary } from '@/api/hooks';
import { formatRelative } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';

export function CredentialDialog({
  service,
  hint,
  open,
  onClose,
}: {
  service: ServiceSummary;
  hint?: string;
  open: boolean;
  onClose: () => void;
}) {
  const size = useControlSize();
  const save = useOperation<{ serviceId: string; secret: string }, { id: string; maskedHint: string }>(
    'credential.set',
  );
  const check = useOperation<
    { serviceId: string },
    { ok: boolean; scope: string; message: string }
  >('credential.check');
  const remove = useOperation<{ serviceId: string }, { ok: boolean }>('credential.delete');

  const [secret, setSecret] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSecret('');
    setLocalError(null);
    save.reset();
    check.reset();
    remove.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, service.id]);

  const serverError =
    save.error instanceof ApiError
      ? save.error.message
      : remove.error instanceof ApiError
        ? remove.error.message
        : null;

  const checkResult = check.data;

  function submit() {
    setLocalError(null);
    if (secret.trim().length < 8) {
      setLocalError('Вставьте ключ целиком');
      return;
    }
    save.mutate({ serviceId: service.id, secret: secret.trim() }, { onSuccess: () => setSecret('') });
  }

  return (
    <Dialog
      open={open}
      title={service.credential ? 'Заменить ключ доступа' : 'Задать ключ доступа'}
      description={`Ключ сервиса «${service.name}». Хранится зашифрованным, показывается только маской`}
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button variant="secondary" size={size} onClick={onClose}>
            Закрыть
          </Button>
          <Button size={size} onClick={submit} loading={save.isPending} disabled={secret.length === 0}>
            {service.credential ? 'Заменить' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        {service.credential ? (
          <div
            style={{
              background: 'var(--surface-sunken)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-5)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{ fontFamily: 'var(--font-mono)', fontSize: 15, color: 'var(--text-strong)' }}
              >
                {service.credential.maskedHint}
              </span>
              {service.credential.isValid === true ? (
                <Badge tone="success" dot>
                  Ключ работает
                </Badge>
              ) : service.credential.isValid === false ? (
                <Badge tone="danger" dot>
                  Ключ не принят
                </Badge>
              ) : (
                <Badge tone="neutral">Не проверялся</Badge>
              )}
            </div>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Provenance>
                {service.credential.lastCheckedAt
                  ? `Проверен ${formatRelative(service.credential.lastCheckedAt)}`
                  : 'Ещё не проверялся'}
              </Provenance>
            </div>
            <div style={{ marginTop: 'var(--space-4)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
              <Button
                variant="secondary"
                size="sm"
                loading={check.isPending}
                onClick={() => check.mutate({ serviceId: service.id })}
              >
                Проверить ключ
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={remove.isPending}
                onClick={() => remove.mutate({ serviceId: service.id }, { onSuccess: onClose })}
              >
                Удалить ключ
              </Button>
            </div>
            {checkResult ? (
              <div
                style={{
                  marginTop: 'var(--space-4)',
                  font: '400 13px/1.5 var(--font-text)',
                  color: checkResult.ok ? 'var(--green-600)' : 'var(--status-danger-fg)',
                }}
              >
                {checkResult.message}
              </div>
            ) : null}
          </div>
        ) : null}

        <Input
          size={size}
          label={service.credential ? 'Новый ключ' : 'Ключ'}
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          help={hint}
          {...(localError ?? serverError ? { error: (localError ?? serverError) as string } : {})}
        />

        {save.isSuccess ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              font: '400 13px var(--font-text)',
              color: 'var(--green-600)',
            }}
          >
            <Icon name="Check" size={16} color="var(--green-600)" />
            Ключ сохранён, маска {save.data?.maskedHint}
          </div>
        ) : null}

        <Provenance>
          Старое значение перезаписывается, история значений не хранится. Отозвать ключ можно только
          в кабинете провайдера: приложение этого не умеет и не изображает обратного.
        </Provenance>
      </div>
    </Dialog>
  );
}
